import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import LoginForm from './LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import { ILoginPageState } from '../../types/stateTypes';
import { ILoginPageProps } from '../../types/propTypes';
import { Common } from '../../utils/constants';
import { IAuthResponse, ISupportedLanguage, IUserData } from '../../models/login';
import ResetPasswordForm from './ResetPasswordForm';
import QRCodeScannerPage from './QRCodeScannerPage';
import LoginAPIService from '../../services/login/loginApi-service';
import { toast } from 'react-toastify';
import {
    changeLanguage,
    saveUserData,
    setCurrencyDetails,
    setCurrencyFormat,
    setDateTimeFormat,
    setDefaultLanguage,
    setNumberFormat,
    setSupportedLanguages,
} from '../../store/actions/app.Actions';
import { store } from '../../store';
import { defaultNumberFormat } from '../../components/common/LocalNumberFormat';
import _ from 'lodash';
import { companyNameReplace } from '../../utils';

let isLogin = false;
let counter = 0;
let apiError = false;
class LoginPage extends React.Component<ILoginPageProps & WithTranslation, ILoginPageState> {
    state: ILoginPageState;
    loginApiService: LoginAPIService;
    constructor(props: ILoginPageProps & WithTranslation) {
        super(props);
        this.state = {
            user: null,
            stage: Common.AuthStage.SIGNIN,
            qrString: '',
            email: '',
            languages: [],
        };
        this.loginApiService = new LoginAPIService();
    }

    handleChangeStage = (stage: number, email?: string) => {
        this.setState({ stage, email });
    };

    CommonChallengeName = async (data: IAuthResponse) => {
        switch (data.status) {
            case Common.AuthChallengeName.SUCCESS:
                // store user and redirect to home
                this.setState({ user: data.user });
                const inputData = {
                    is_user_login: true,
                };
                await this.loginApiService
                    .userDetails(inputData)
                    .then((userData: IUserData) => {
                        isLogin = true;
                        this.props.dispatch(changeLanguage(userData?.language_preference[0]!));
                        this.props.dispatch(saveUserData({ ...userData, ...data.user }));
                        this.props.dispatch(setCurrencyDetails(userData?.currency_details));
                        if (userData && userData.terms_and_condition_required) {
                            this.props.history.push('/termsAndConditions');
                        } else {
                            this.props.history.push('/');
                        }
                    })
                    .catch((m) => {
                        console.log('m login catch', m);
                        if (
                            m.response &&
                            m.response.data &&
                            m.response.data.message &&
                            m.response.data.message.includes('Organisation is deleted')
                        ) {
                            toast.error(m.response.data.message);
                        } else if (m && m.includes('Organisation is deleted')) {
                            toast.error(m.toString());
                        } else {
                            toast.error(this.props.t('login.errors.common'));
                        }
                        this.props.logout();
                    });
                await this.loginApiService
                    .getRegionConfigurations()
                    .then((res) => {
                        if (res?.data) {
                            this.props.dispatch(setDateTimeFormat(res?.data?.date_time_formats));
                            this.props.dispatch(setCurrencyFormat(res?.data?.currency_formats));
                            if (_.isEmpty(res?.data?.number_formats)) {
                                this.props.dispatch(setNumberFormat(defaultNumberFormat));
                            } else {
                                this.props.dispatch(setNumberFormat(res?.data?.number_formats));
                            }
                        }
                    })
                    .catch((e: any) => {
                        this.props.dispatch(setDateTimeFormat({}));
                        this.props.dispatch(
                            setNumberFormat({
                                GENERAL_NUMBER_FORMAT: '###.###.###,##',
                                FINANCIAL_NUMBER_FORMAT: '###.###.###,####',
                                NUMBER_DECIMAL_SEPARATOR: ',',
                            }),
                        );
                    });
                break;
            case Common.AuthChallengeName.MFA_SETUP:
                const str = `otpauth://totp/InstaVolt-${process.env.REACT_APP_PORTAL_REGION}-${
                    process.env.REACT_APP_STAGE
                }(${data.email || this.state.email})?secret=${data.code}&issuer=InstaVolt`;
                this.setState({ stage: Common.AuthStage.QR_SCAN, user: data.user, qrString: str });
                break;
            case Common.AuthChallengeName.SOFTWARE_TOKEN_MFA:
                this.setState({ stage: Common.AuthStage.VERIFICATION_CODE, user: data.user });
                break;
            case Common.AuthChallengeName.NEW_PASSWORD_REQUIRED:
                this.setState({ stage: Common.AuthStage.CHANGE_PASSWORD, user: data.user, email: data.email });
                break;
            default:
                break;
        }
    };

    handleAuthUser = (response: IAuthResponse) => {
        this.CommonChallengeName(response);
    };

    componentDidMount = async () => {
        //added counter to reduce num of api calls before login
        if (counter === 0) {
            counter++;
            try {
                (document as any).getElementById('loading').classList.remove('spinner-disable');
                let defaultLang;
                await this.loginApiService.getSupportedLanguages().then((res) => {
                    defaultLang =
                        res?.data?.rows &&
                        res.data.rows.length > 0 &&
                        res.data.rows.filter((item) => item.is_default)[0];
                    if (res?.data?.rows) {
                        this.props.dispatch(setSupportedLanguages(res.data.rows));
                        this.setState({
                            languages: res.data.rows,
                        });
                    }
                });

                const queryStringLang = new URLSearchParams(window.location.search);
                if (
                    !isLogin &&
                    (queryStringLang.get('language') === '' ||
                        queryStringLang.get('language') === null ||
                        queryStringLang.get('language') === undefined) &&
                    store.getState()?.UserData?.language.code !== defaultLang?.code
                ) {
                    this.props.dispatch(changeLanguage(defaultLang));
                    this.props.dispatch(setDefaultLanguage(defaultLang));
                }
                (document as any).getElementById('loading').classList.add('spinner-disable');
            } catch (e: any) {
                (document as any).getElementById('loading').classList.add('spinner-disable');
                apiError = true;
                throw e;
            }
        }
    };

    render() {
        return (
            <>
                <Helmet>
                    <title>{companyNameReplace(this.props.t('login.title'))}</title>
                </Helmet>
                <div className="login-page p-0 no-gutters container-fluid clearfix">
                    <Row className="row no-gutters min-h-100 align-items-center">
                        <Col>
                            {(this.state.languages?.length > 0 ||
                                apiError ||
                                store.getState().LanguageInfo?.supportedLanguages?.length > 0) && (
                                <div className="login-outer">
                                    {this.state.stage === Common.AuthStage.SIGNIN && (
                                        <LoginForm
                                            t={this.props.t}
                                            onChangeStage={this.handleChangeStage}
                                            onAuthUser={this.handleAuthUser}
                                        />
                                    )}
                                    {this.state.stage === Common.AuthStage.FORGOT_PASSWORD && (
                                        <ForgotPasswordForm t={this.props.t} onChangeStage={this.handleChangeStage} />
                                    )}
                                    {(this.state.stage === Common.AuthStage.CHANGE_PASSWORD ||
                                        this.state.stage ===
                                            Common.AuthStage.CHANGE_PASSWORD_WITH_VERIFICATION_CODE) && (
                                        <ResetPasswordForm
                                            t={this.props.t}
                                            user={this.state.user}
                                            email={this.state.email}
                                            stage={this.state.stage}
                                            onChangeStage={this.handleChangeStage}
                                            onAuthUser={this.handleAuthUser}
                                        />
                                    )}
                                    {(this.state.stage === Common.AuthStage.QR_SCAN ||
                                        this.state.stage === Common.AuthStage.VERIFICATION_CODE) && (
                                        <QRCodeScannerPage
                                            t={this.props.t}
                                            user={this.state.user}
                                            stage={this.state.stage}
                                            qrString={this.state.qrString}
                                            onAuthUser={this.handleAuthUser}
                                        />
                                    )}
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            </>
        );
    }
}

export default connect(null, null)(withTranslation()(LoginPage));
